import { Circle, CircleHalf, CheckCircle, Star } from '@phosphor-icons/react';
import { HeaderContent, HEADER_TITLE_CLASS } from '../../components/shell/app-header';
import { COMPETENCES, COMPETENCE_LEVELS } from '../../data/student-dummy';

const LEVEL_COLOR: Record<number, string> = {
  0: 'var(--naia-muted-2)',
  1: 'var(--naia-level-1)',
  2: 'var(--naia-level-2)',
  3: 'var(--naia-level-3)',
  4: 'var(--naia-level-4)',
};

function LevelIcon({ level }: { level: number }) {
  const color = LEVEL_COLOR[level];
  const props = { size: 16, style: { color }, 'aria-hidden': true as const };
  if (level === 0) return <Circle          {...props} weight="regular" />;
  if (level === 1) return <CircleHalf      {...props} weight="regular" />;
  if (level === 2) return <CheckCircle     {...props} weight="regular" />;
  if (level === 3) return <CheckCircle     {...props} weight="fill"    />;
  return               <Star              {...props} weight="fill"    />;
}

const LEVEL_TEXT_CLASS: Record<number, string> = {
  0: 'text-muted-foreground',
  1: 'text-naia-level-1',
  2: 'text-naia-level-2',
  3: 'text-naia-level-3',
  4: 'text-naia-level-4',
};

export default function Competences() {
  const byDomain = COMPETENCES.reduce<Record<string, typeof COMPETENCES>>(
    (acc, c) => { (acc[c.domain] ??= []).push(c); return acc; },
    {},
  );

  const acquired   = COMPETENCES.filter((c) => c.level >= 2).length;
  const inProgress = COMPETENCES.filter((c) => c.level === 1).length;
  const total      = COMPETENCES.length;
  const pct        = Math.round((acquired / total) * 100);

  return (
    <>
      <HeaderContent width="md" showBurger backTo="/mini-apps" backLabel="Mini-apps">
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Compétences</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">Acquises au fil de tes parcours</p>
        </div>
      </HeaderContent>
      <main className="max-w-[880px] mx-auto px-5 pb-16 pt-4">


        {/* Stats */}
        <div className="bg-card border border-border rounded-card px-5 py-4 mb-7 flex flex-col gap-3">
          <div className="flex items-end justify-between gap-3">
            <div className="flex gap-4">
              <div className="flex flex-col gap-0.5">
                <span className="text-[28px] font-black text-naia-green leading-none tabular-nums">{acquired}</span>
                <span className="text-[11px] text-muted-foreground font-medium">acquises</span>
              </div>
              <div className="w-px bg-border self-stretch" />
              <div className="flex flex-col gap-0.5">
                <span className="text-[28px] font-black text-naia-blue leading-none tabular-nums">{inProgress}</span>
                <span className="text-[11px] text-muted-foreground font-medium">en cours</span>
              </div>
              <div className="w-px bg-border self-stretch" />
              <div className="flex flex-col gap-0.5">
                <span className="text-[28px] font-black text-foreground leading-none tabular-nums">{total}</span>
                <span className="text-[11px] text-muted-foreground font-medium">au total</span>
              </div>
            </div>
            <span className="text-[15px] font-extrabold text-naia-green tabular-nums">{pct}%</span>
          </div>
          <div className="h-[6px] rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-naia-green transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Domains */}
        <div className="flex flex-col gap-7">
          {Object.entries(byDomain).map(([domain, items]) => (
            <section key={domain}>
              <div className="flex items-center gap-2.5 mb-3">
                <span className="text-[10.5px] font-extrabold tracking-[1.5px] text-muted-foreground uppercase">
                  {domain}
                </span>
                <div className="flex-1 h-px bg-border" />
              </div>

              <div className="rounded-card border border-border overflow-hidden divide-y divide-border">
                {items.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 bg-card px-4 py-3.5">
                    <LevelIcon level={c.level} />
                    <span className="flex-1 text-[14px] font-medium text-foreground leading-snug">
                      {c.label}
                    </span>
                    <span className={`text-[11.5px] font-semibold ${LEVEL_TEXT_CLASS[c.level]}`}>
                      {COMPETENCE_LEVELS[c.level]}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>
    </>
  );
}
